import React, { useCallback, useMemo, useRef, useState } from 'react';
import clsx from 'clsx';
import { useRouter } from 'next/router';
import { Transition } from '@headlessui/react';
import Link from '@b2b-frontend/components/Link';
import Logo from '@b2b-frontend/components/icons/Logo';
import ContactIcon from '@b2b-frontend/components/icons/ContactIcon';
import { IUser } from '@b2b-frontend/types';
import MaxWidthContainer from '../MaxWidthContainer';
import DropdownNav from '../DropdownNav';
import { ROUTES } from '../../constants';
import MyAccountMenu from './MyAccountMenu';
import SearchPopover from './SearchPopover';
import LorealAccountButton from './LorealAccountButton';
import { useGTMDispatch } from '@elgorditosalsero/react-gtm-hook';
import { createHeaderMenuClickEvent } from '@b2b-frontend/utils/analytics/gtm';

interface IDesktopNavProps {
	isLoading: boolean;
	user: IUser;
	basketSlot: React.ReactNode;
}

const DesktopNav: React.FC<IDesktopNavProps> = ({ user, basketSlot }) => {
	const { pathname } = useRouter();
	const [isDropdownNavOpen, setIsDropdownNavOpen] = useState(false);
	const dropdownMenuButtonRef = useRef<HTMLButtonElement>(null);
	const sendDataToGTM = useGTMDispatch();

	const showBasket = useMemo(() => {
		return !!user && pathname !== '/checkout';
	}, [user, pathname]);

	const handleActiveNavCloseRequested = () => {
		setIsDropdownNavOpen(false);
		dropdownMenuButtonRef.current?.focus();
	};

	const handlePopoverMenuButtonKeyUp = (event: React.KeyboardEvent) => {
		const key = event.key;

		switch (key) {
			case 'ArrowDown':
				event.preventDefault();
				setIsDropdownNavOpen(true);

				break;
		}
	};

	return (
		<>
			<MaxWidthContainer className="px-4 py-4 md:px-4 md:py-4 flex items-center justify-start">
				<Link href="/">
					<div className="sr-only">Go home</div>
					<Logo className="ml-0 md:w-full" />
				</Link>
				<ul className="ml-0 md:ml-auto flex items-center text-xs justify-start">
					<li className="mr-4 lg:mr-10">
						<SearchPopover />
					</li>
					<li className="mr-4 lg:mr-10">
						<Link
							href={ROUTES.HELP.CONTACT_US}
							className="inline-flex items-center text-black hover:text-brand"
							linkProps={{
								onClick: () => {
									sendDataToGTM(
										createHeaderMenuClickEvent({
											clickArea: 'top-level menu',
											label: 'Contact',
										}),
									);
								},
							}}
						>
							Contact <ContactIcon className="ml-2" />
						</Link>
					</li>
					<li className="mr-4 lg:mr-10">
						<MyAccountMenu />
					</li>
					{showBasket && <li className="mr-4 lg:mr-10">{basketSlot}</li>}
					<li className="mr-4 lg:mr-10">
						<LorealAccountButton />
					</li>
				</ul>
			</MaxWidthContainer>
			<nav className="flex relative h-16 border-t border-b border-gray w-full text-sm px-6 lg:text-base items-center justify-between lg:justify-center font-serif font-bold">
				<div className="lg:mr-44">
					<button
						onClick={() => setIsDropdownNavOpen(true)}
						onKeyDown={handlePopoverMenuButtonKeyUp}
						className={clsx(
							'font-bold',
							isDropdownNavOpen ? 'text-brand' : 'text-black',
						)}
						aria-haspopup="true"
						aria-controls="brand_popup"
						aria-expanded={isDropdownNavOpen}
						id="brand_popover_control"
						ref={dropdownMenuButtonRef}
					>
						Browse by brand
					</button>
				</div>
				<div className="relative lg:mr-44">
					<Link
						href={ROUTES.LEARN.ROOT}
						linkProps={{
							onClick: () => {
								sendDataToGTM(
									createHeaderMenuClickEvent({
										clickArea: 'second-level menu',
										label: 'Learn',
									}),
								);
							},
						}}
					>
						Learn
					</Link>
				</div>
				<Link
					href={ROUTES.WHATS_NEW.ROOT}
					linkProps={{
						onClick: () => {
							sendDataToGTM(
								createHeaderMenuClickEvent({
									clickArea: 'second-level menu',
									label: `What's New?`,
								}),
							);
						},
					}}
				>
					What&apos;s new?
				</Link>
				<Transition
					className="absolute transform translate-y-16 top-0 z-20 left-0 lg:left-1/2 lg:-translate-x-1/2 w-screen max-w-[1364px]"
					enter="transition ease-out duration-100"
					enterFrom="transform opacity-0 scale-95"
					enterTo="transform opacity-100 scale-100"
					leave="transition ease-in duration-75"
					leaveFrom="transform opacity-100 scale-100"
					leaveTo="transform opacity-0 scale-95"
					show={isDropdownNavOpen}
				>
					<DropdownNav
						onCloseRequested={handleActiveNavCloseRequested}
						isOpen={isDropdownNavOpen}
					/>
				</Transition>
			</nav>
		</>
	);
};

export default DesktopNav;
